import { NotificationType } from '@affine/graphql';

type BudgetAlertScope =
  | 'self'
  | 'member'
  | 'automation'
  | 'workflow'
  | 'workflow_cap';

const BUDGET_SCOPES: readonly BudgetAlertScope[] = [
  'self',
  'member',
  'automation',
  'workflow',
  'workflow_cap',
];

/** The translations the budget and credit notices use. */
export interface BudgetAlertTranslator {
  'com.streamlain.notification.budget-alert.self'(params: {
    threshold: string;
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.self-block'(params: {
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.member'(params: {
    name: string;
    threshold: string;
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.automation'(params: {
    name: string;
    threshold: string;
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.workflow'(params: {
    name: string;
    threshold: string;
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.workflow-cap'(params: {
    threshold: string;
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.workflow-cap-block'(params: {
    used: string;
    cap: string;
  }): string;
  'com.streamlain.notification.budget-alert.credit-low'(params: {
    remaining: string;
    credits: string;
  }): string;
  'com.streamlain.notification.budget-alert.bandwidth'(params: {
    threshold: string;
    usedGB: string;
  }): string;
  'com.streamlain.notification.budget-alert.unnamed'(): string;
}

export interface BudgetAlertCopy {
  readonly text: string;
  /** Reached the limit or the last credit step. */
  readonly severe: boolean;
  /** Settings tab that shows the limit. */
  readonly settingsTab: 'workspace:ai' | 'workspace:billing';
}

const BUDGET_ALERT_TYPES: ReadonlySet<string> = new Set([
  NotificationType.BudgetThresholdAi,
  NotificationType.BudgetThresholdAutomations,
  NotificationType.BudgetThresholdBandwidth,
  NotificationType.CreditBalanceLow,
]);

export function isBudgetAlertType(type: string): boolean {
  return BUDGET_ALERT_TYPES.has(type);
}

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * The line a budget or credit notice shows; null when the body does not
 * fit its type.
 */
export function budgetAlertCopy(
  t: BudgetAlertTranslator,
  type: string,
  body: unknown,
  locale?: string
): BudgetAlertCopy | null {
  const value = (body ?? {}) as Record<string, unknown>;
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const format = (n: number) => number.format(n);

  if (type === NotificationType.CreditBalanceLow) {
    const { thresholdPct, remainingPercent, remainingCredits } = value;
    if (
      !isNumber(thresholdPct) ||
      !isNumber(remainingPercent) ||
      !isNumber(remainingCredits)
    ) {
      return null;
    }
    return {
      text: t['com.streamlain.notification.budget-alert.credit-low']({
        remaining: format(remainingPercent),
        credits: format(remainingCredits),
      }),
      severe: thresholdPct <= 10,
      settingsTab: 'workspace:billing',
    };
  }

  if (type === NotificationType.BudgetThresholdBandwidth) {
    const { thresholdPct, usedGB } = value;
    if (!isNumber(thresholdPct) || !isNumber(usedGB)) return null;
    return {
      text: t['com.streamlain.notification.budget-alert.bandwidth']({
        threshold: format(thresholdPct),
        usedGB: format(usedGB),
      }),
      severe: thresholdPct >= 100,
      settingsTab: 'workspace:billing',
    };
  }

  if (
    type !== NotificationType.BudgetThresholdAi &&
    type !== NotificationType.BudgetThresholdAutomations
  ) {
    return null;
  }
  const { scope, thresholdPct, usedCredits, capCredits, behavior } = value;
  if (
    !BUDGET_SCOPES.includes(scope as BudgetAlertScope) ||
    !isNumber(thresholdPct) ||
    !isNumber(usedCredits) ||
    !isNumber(capCredits)
  ) {
    return null;
  }
  const blocked = thresholdPct >= 100 && behavior === 'block';
  const amounts = {
    threshold: format(thresholdPct),
    used: format(usedCredits),
    cap: format(capCredits),
  };
  const name =
    typeof value.subjectName === 'string' && value.subjectName
      ? value.subjectName
      : t['com.streamlain.notification.budget-alert.unnamed']();
  const text = {
    self: () =>
      blocked
        ? t['com.streamlain.notification.budget-alert.self-block'](amounts)
        : t['com.streamlain.notification.budget-alert.self'](amounts),
    member: () =>
      t['com.streamlain.notification.budget-alert.member']({
        ...amounts,
        name,
      }),
    automation: () =>
      t['com.streamlain.notification.budget-alert.automation']({
        ...amounts,
        name,
      }),
    workflow: () =>
      t['com.streamlain.notification.budget-alert.workflow']({
        ...amounts,
        name,
      }),
    workflow_cap: () =>
      blocked
        ? t['com.streamlain.notification.budget-alert.workflow-cap-block'](
            amounts
          )
        : t['com.streamlain.notification.budget-alert.workflow-cap'](amounts),
  }[scope as BudgetAlertScope]();
  return {
    text,
    severe: thresholdPct >= 100,
    settingsTab: 'workspace:ai',
  };
}
